import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DateInput } from './ui/date-input';
import { Calendar, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const DoctorLeaveManagement = ({ leaveDates = [], onUpdate }) => {
  const [leaveType, setLeaveType] = useState('single'); // 'single' or 'range'
  const [singleDate, setSingleDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Helper to get all dates in a range
  const getDateRange = (start, end) => {
    const dates = [];
    const startD = new Date(start);
    const endD = new Date(end);
    
    while (startD <= endD) {
      dates.push(startD.toISOString().split('T')[0]);
      startD.setDate(startD.getDate() + 1);
    }
    return dates;
  };

  const handleAddLeave = () => {
    let newDates = [];
    
    if (leaveType === 'single') {
      if (!singleDate) {
        toast.error('Please select a date');
        return;
      }
      newDates = [singleDate];
    } else {
      if (!startDate || !endDate) {
        toast.error('Please select both start and end dates');
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        toast.error('Start date must be before end date');
        return;
      }
      newDates = getDateRange(startDate, endDate);
    }

    // Add new dates and remove duplicates
    const updatedDates = [...new Set([...leaveDates, ...newDates])].sort();
    onUpdate(updatedDates);
    
    // Reset form
    setSingleDate('');
    setStartDate('');
    setEndDate('');
    
    toast.success(`Leave added for ${newDates.length} day(s)`);
  };

  const handleRemoveDate = (dateToRemove) => {
    const updatedDates = leaveDates.filter(d => d !== dateToRemove);
    onUpdate(updatedDates);
    toast.success('Leave date removed');
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-4">
      {/* Add Leave Section */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center space-x-2 mb-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <h4 className="font-semibold text-gray-900">Add Leave</h4>
        </div>

        {/* Leave Type Selector */}
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setLeaveType('single')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              leaveType === 'single'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Single Date
          </button>
          <button
            type="button"
            onClick={() => setLeaveType('range')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              leaveType === 'range'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Date Range
          </button>
        </div>

        {/* Single Date Input */}
        {leaveType === 'single' && (
          <div>
            <Label htmlFor="single_date">Select Date</Label>
            <DateInput
              id="single_date"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
              className="mt-1"
            />
          </div>
        )}

        {/* Date Range Inputs */}
        {leaveType === 'range' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <DateInput
                id="start_date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <DateInput
                id="end_date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        )}

        <Button
          type="button"
          onClick={handleAddLeave}
          className="w-full bg-blue-600 hover:bg-blue-700"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Leave
        </Button>
      </div>

      {/* Leave Dates List */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-2">Leave Dates ({leaveDates.length})</h4>
        {leaveDates.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No leave dates scheduled</p>
        ) : (
          <div className="max-h-60 overflow-y-auto space-y-1">
            {leaveDates.map((date) => (
              <div
                key={date}
                className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50"
              >
                <span className="text-sm text-gray-700">{formatDisplayDate(date)}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveDate(date)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Remove leave date"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorLeaveManagement;
